/**
 * Where to land after hosted WorkOS authentication. AuthKit always redirects to the configured
 * redirect URI, so the intended in-app destination travels in the AuthKit `state` and is
 * restored only if it is a same-origin path.
 */
const RETURN_TO_STORAGE_KEY = "cortex:auth-return-to";
const AUTH_ONLY_PATHS = new Set(["/login", "/callback"]);
export const INVITATION_TOKEN_PARAM = "invitation_token";

export function sanitizeReturnTo(value: unknown, origin = window.location.origin): string | null {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || AUTH_ONLY_PATHS.has(url.pathname)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

export function currentReturnTo(): string | null {
  const { pathname, search, hash } = window.location;
  return sanitizeReturnTo(`${pathname}${search}${hash}`);
}

/** AuthKit `state` for a sign-in/sign-up redirect, also kept for this tab as a fallback. */
export function authRedirectState(returnTo = currentReturnTo()) {
  try {
    if (returnTo) window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, returnTo);
  } catch {
    // Storage can be unavailable (private mode); the AuthKit state still carries it.
  }
  return returnTo ? { returnTo } : undefined;
}

/** Destination after the AuthKit redirect callback; consumes the stored fallback. */
export function resolveReturnTo(state: unknown): string | null {
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    stored = null;
  }
  const fromState =
    typeof state === "object" && state !== null && "returnTo" in state ? state.returnTo : undefined;
  return sanitizeReturnTo(fromState) ?? sanitizeReturnTo(stored);
}

export function getInvitationToken(search = window.location.search): string | null {
  const token = new URLSearchParams(search).get(INVITATION_TOKEN_PARAM)?.trim();
  return token ? token : null;
}

export function invitationPath(token: string) {
  return `/invite?${new URLSearchParams({ [INVITATION_TOKEN_PARAM]: token }).toString()}`;
}
